import { Contact, CreateContactInput, Household, UpdateContactInput } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  bulkCreateContacts,
  BulkCreateContactsPayload,
  bulkDestroyContacts,
  bulkMoveContactsToBook,
  BulkMoveContactsToBookParams,
  bulkUpdateContacts,
  BulkUpdateContactsPayload,
  convertContactToHousehold,
  createContact,
  deleteContact,
  mergeContact,
  MergeContactResponse,
  updateContact,
} from "./api"

interface CreateContactMutationParams {
  contact: CreateContactInput
  overrideBookId?: string
}

export function useCreateContact(options?: {
  onSuccess?: (contact: Contact, variables: CreateContactMutationParams, context: unknown) => void
  onError?: (error: Error, variables: CreateContactMutationParams, context: unknown) => void
  skipDefaultSuccessToast?: boolean
}) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ contact, overrideBookId }: CreateContactMutationParams) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return createContact(context, contact, overrideBookId)
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
      queryClient.invalidateQueries({ queryKey: ["contacts", "infinite"] })
      queryClient.invalidateQueries({ queryKey: ["business"] })
      if (!options?.skipDefaultSuccessToast) {
        toast.success("Contact created successfully")
      }
      options?.onSuccess?.(data, variables, context)
    },
    onError: (error, variables, context) => {
      options?.onError?.(error, variables, context)
    },
  })
}

export function useUpdateContact(contactId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: UpdateContactInput) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return updateContact(context, contactId, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact", contactId] })
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
      queryClient.invalidateQueries({ queryKey: ["business"] })
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ["contact", contactId] })
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
    },
  })
}

export function useDeleteContact() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (contactId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return deleteContact(context, contactId)
    },
    meta: { errorToast: { fallback: "Failed to delete contact" } },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
      queryClient.invalidateQueries({ queryKey: ["business"] })
      toast.success("Contact deleted successfully")
    },
  })
}

export interface MergeContactsParams {
  source_contact_id: string
  target_contact_id: string
}

export function useMergeContacts() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<MergeContactResponse, Error, MergeContactsParams>({
    mutationFn: async ({ source_contact_id, target_contact_id }: MergeContactsParams) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return mergeContact(context, source_contact_id, target_contact_id)
    },
    meta: { errorToast: { fallback: "Failed to merge contacts" } },
    onSuccess: (_data, { source_contact_id }) => {
      queryClient.removeQueries({ queryKey: ["contact", source_contact_id] })
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
      queryClient.invalidateQueries({ queryKey: ["contact"] })
      queryClient.invalidateQueries({ queryKey: ["business"] })
    },
  })
}

export function useBulkUpdateContacts() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkUpdateContactsPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkUpdateContacts(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
      queryClient.invalidateQueries({ queryKey: ["business"] })
    },
  })
}

export function useBulkDestroyContacts() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (contactIds: string[]) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkDestroyContacts(context, contactIds)
    },
    meta: { errorToast: { fallback: "Failed to delete contacts" } },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
      queryClient.invalidateQueries({ queryKey: ["business"] })
    },
  })
}

export function useBulkMoveContactsToBook() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkMoveContactsToBookParams) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkMoveContactsToBook(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
      queryClient.invalidateQueries({ queryKey: ["contacts", "infinite"] })
    },
  })
}

export function useBulkCreateContacts() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkCreateContactsPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkCreateContacts(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
      queryClient.invalidateQueries({ queryKey: ["contacts", "infinite"] })
      queryClient.invalidateQueries({ queryKey: ["lists"] })
      queryClient.invalidateQueries({ queryKey: ["business"] })
      toast.success("Contact import jobs queued successfully")
    },
  })
}

type HouseholdType = "Prospect" | "Client"

function useConvertContactToHousehold(
  contactId: string,
  householdType: HouseholdType,
  queryKey: string,
  onSuccess?: (data: Household) => void
) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<Household>({
    mutationFn: async (): Promise<Household> => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return convertContactToHousehold(context, contactId, householdType)
    },
    onSuccess: (data) => {
      queryClient.removeQueries({ queryKey: ["contact", contactId] })
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
      queryClient.invalidateQueries({ queryKey: ["households"] })
      queryClient.invalidateQueries({ queryKey: [queryKey] })
      queryClient.invalidateQueries({ queryKey: ["business"] })
      onSuccess?.(data)
    },
  })
}

export function useConvertContactToProspect(contactId: string, onSuccess?: (data: Household) => void) {
  return useConvertContactToHousehold(contactId, "Prospect", "prospects", onSuccess)
}

export function useConvertContactToClient(contactId: string, onSuccess?: (data: Household) => void) {
  return useConvertContactToHousehold(contactId, "Client", "clients", onSuccess)
}
